import React, { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";

import { titleConfig } from "../configs/titleConfig";
import { useSiteContext } from "../contexts/SiteContext";

interface TitleParts {
  primary?: string;
  secondary?: string;
  tertiary?: string;
  fallback?: string;
}

/**
 * Builds a hierarchical title from parts, filtering out empty values
 */
const buildTitle = (
  parts: TitleParts,
  separator: string = titleConfig.separator,
): string => {
  const titleParts = [
    parts.primary,
    parts.secondary,
    parts.tertiary,
    parts.fallback,
    titleConfig.siteName,
  ].filter(Boolean);

  return titleParts.join(` ${separator} `);
};

/**
 * Core hook for managing page titles with hierarchical structure
 * Handles all common logic for title management
 */
const usePageTitleCore = (titleParts: TitleParts) => {
  const siteContext = useSiteContext();
  const { team } = useTeam();

  const formattedTitle = useMemo(() => {
    // Get site domain from context
    const siteDomain = siteContext?.site?.site?.domain;

    // Build title with available parts, only adding fallbacks when primary is missing
    const parts: TitleParts = {
      ...titleParts,
      tertiary: titleParts.tertiary || siteDomain,
      // Only use team name as fallback if no primary title is provided
      fallback: titleParts.primary
        ? titleParts.fallback
        : titleParts.fallback || team?.name,
    };

    return buildTitle(parts);
  }, [titleParts, siteContext?.site?.site?.domain, team?.name]);

  // Set the document title
  useEffect(() => {
    document.title = formattedTitle;
  }, [formattedTitle]);

  // Return the Helmet component for more advanced usage
  return {
    title: formattedTitle,
    HelmetComponent: () =>
      React.createElement(
        Helmet,
        null,
        React.createElement("title", null, formattedTitle),
      ),
  };
};

/**
 * Hook for regular pages.
 * Uses pattern: "$title : $brand", or "$title : $site_domain : $brand" inside
 * a site (the domain comes from SiteContext). With an empty title the team
 * name takes its place.
 */
export const usePageTitle = (title: string) => {
  return usePageTitleCore({
    primary: title,
  });
};

/**
 * Hook for link detail pages that includes link domain, link type, and site domain in the title
 * Uses pattern: "$link_domain : $link_type : $site_domain : $brand"
 * Falls back gracefully if any context is missing
 */
export const useLinkDetailPageTitle = (
  linkDomain?: string,
  linkType?: string,
) => {
  return usePageTitleCore({
    primary: linkDomain,
    secondary: linkType,
  });
};

/**
 * Hook for keyword detail pages that includes keyword name and site domain in the title
 * Uses pattern: "$keyword_name : Keywords : $site_domain : $brand"
 * Falls back gracefully if any context is missing
 */
export const useKeywordDetailPageTitle = (keywordName?: string) => {
  return usePageTitleCore({
    primary: keywordName,
    secondary: PAGE_TITLES.keywords,
  });
};
